import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import SpinWheel from '@/components/SpinWheel';
import { QrCode, Star, BarChart3, Users, Palette, Smartphone, Check, ArrowRight, Shield, Sparkles, Zap, Send, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6 } } };
const stagger = { visible: { transition: { staggerChildren: 0.12 } } };
const scaleIn = { hidden: { opacity: 0, scale: 0.9 }, visible: { opacity: 1, scale: 1, transition: { duration: 0.5 } } };

const DEMO_SLOTS = [
  { id: '1', label: 'Cafe offert', color: '#7C3AED', probability: 20, is_losing: false },
  { id: '2', label: '-10%', color: '#F59E0B', probability: 25, is_losing: false },
  { id: '3', label: 'Perdu', color: '#6B21A8', probability: 20, is_losing: true },
  { id: '4', label: 'Dessert offert', color: '#10B981', probability: 15, is_losing: false },
  { id: '5', label: 'Perdu', color: '#1E3A5F', probability: 10, is_losing: true },
  { id: '6', label: 'Boisson offerte', color: '#EC4899', probability: 10, is_losing: false },
];

const FEATURES = [
  { icon: Palette, title: "Roue 100% personnalisable", desc: "Lots, couleurs, probabilites, duree de validite — vous controlez tout depuis votre dashboard.", color: "#7C3AED" },
  { icon: QrCode, title: "QR Code pret a imprimer", desc: "Generez et telechargez votre QR code unique en un clic. Collez-le a votre caisse.", color: "#F59E0B" },
  { icon: Star, title: "Google, Instagram, Facebook", desc: "Avis Google, abonnes Insta ou Facebook — vos clients choisissent avant de jouer.", color: "#0284C7" },
  { icon: BarChart3, title: "Statistiques temps reel", desc: "Suivez chaque spin, chaque avis, chaque abonne. Export CSV inclus.", color: "#10B981" },
  { icon: Users, title: "Base clients automatique", desc: "Collectez nom, telephone ou email de chaque joueur. Votre fichier client se remplit tout seul.", color: "#EC4899" },
  { icon: Send, title: "Campagnes Email & SMS", desc: "Envoyez des promotions directement a vos clients depuis votre dashboard grace aux donnees recoltees.", color: "#0284C7" },
  { icon: Shield, title: "Systeme anti-triche", desc: "Codes uniques par lot gagnant, validables une seule fois. Impossible de tricher.", color: "#EF4444" }
];

const TESTIMONIALS = [
  {
    name: "Marie Laurent",
    role: "Cafe de la Place, Lille",
    img: "https://images.unsplash.com/photo-1583198648175-e3d48bb43255?w=150&h=150&fit=crop&crop=face",
    quote: "Depuis qu'on a installe Spin Review, nos avis Google ont explose ! Les clients adorent la roue et en parlent autour d'eux. +200 avis en 3 mois.",
    stars: 5, metric: "+200 avis"
  },
  {
    name: "Marc Lemaire",
    role: "Garage Auto Lemaire, Lyon",
    img: "https://images.unsplash.com/photo-1563244535-471b9fb5a0fa?w=150&h=150&fit=crop&crop=face",
    quote: "Mes abonnes Instagram ont triple en 2 mois. Le QR code est simple a mettre en place et le dashboard est vraiment complet.",
    stars: 5, metric: "x3 abonnes"
  },
  {
    name: "Sophie Martin",
    role: "Salon Elegance, Bordeaux",
    img: "https://images.unsplash.com/photo-1717229772679-1ce20d1db7c5?w=150&h=150&fit=crop&crop=face",
    quote: "Un investissement incroyable ! Pour 99 euros on a un outil marketing qui tourne tout seul. Mes clientes adorent le concept.",
    stars: 5, metric: "ROI x10"
  }
];

function AnimatedCounter({ target, suffix = "" }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const inView = useInView(ref, { once: true });
  useEffect(() => {
    if (!inView) return;
    const duration = 1200;
    const start = performance.now();
    const step = (now) => {
      const progress = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(target);
    };
    requestAnimationFrame(step);
  }, [inView, target]);
  return <span ref={ref}>{count.toLocaleString('fr-FR')}{suffix}</span>;
}

export default function LandingPage() {
  const { user } = useAuth();
  const [loadingPayment, setLoadingPayment] = useState(false);

  const handlePayment = async () => {
    setLoadingPayment(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { console.error(e); setLoadingPayment(false); }
  };

  return (
    <div className="min-h-screen bg-[#FAFAFA]">

      {/* ═══ NAV ═══ */}
      <nav className="fixed top-0 w-full z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50" data-testid="nav">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-12 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5" data-testid="nav-logo">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center font-bold text-sm text-white shadow-lg shadow-purple-500/20">S</div>
            <span className="font-extrabold text-xl text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit' }}>Spin Review</span>
          </Link>
          <div className="flex items-center gap-4">
            {user ? (
              <Link to={user.role === 'admin' ? '/admin' : user.role === 'vendor' ? '/vendor' : '/dashboard'}>
                <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-6 text-sm font-semibold shadow-lg shadow-amber-500/20" data-testid="nav-dashboard-btn">Dashboard</Button>
              </Link>
            ) : (
              <>
                <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900 transition-colors font-medium hidden sm:block" data-testid="nav-login-link">Connexion</Link>
                <Link to="/register">
                  <Button className="bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-6 text-sm font-semibold shadow-lg shadow-amber-500/20" data-testid="nav-register-btn">Commencer</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* ═══ HERO ═══ */}
      <section className="relative pt-28 sm:pt-32 pb-16 px-4 sm:px-6 md:px-12" data-testid="hero-section">
        <div className="max-w-7xl mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            <motion.div initial="hidden" animate="visible" variants={stagger}>
              <motion.div variants={fadeUp} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-amber-200 bg-amber-50 mb-6">
                <Zap className="w-3.5 h-3.5 text-[#F59E0B]" />
                <span className="text-xs font-bold text-amber-700 tracking-wide uppercase">Marketing gamifie pour commercants</span>
              </motion.div>
              <motion.h1 variants={fadeUp} className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.08] text-slate-900 mb-6" style={{ fontFamily: 'Outfit' }}>
                Transformez vos clients en{' '}
                <span className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] bg-clip-text text-transparent">ambassadeurs.</span>
              </motion.h1>
              <motion.p variants={fadeUp} className="text-lg text-slate-600 leading-relaxed max-w-xl mb-8" style={{ fontFamily: 'Poppins' }}>
                La roue cadeaux qui booste vos avis Google et vos abonnes reseaux sociaux — <span className="text-slate-900 font-semibold">en un simple scan QR.</span>
              </motion.p>
              <motion.div variants={fadeUp} className="flex flex-col sm:flex-row flex-wrap gap-3 mb-8">
                <Button onClick={handlePayment} disabled={loadingPayment}
                  className="bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-8 py-6 text-base font-bold shadow-xl shadow-amber-500/25 hover:shadow-amber-500/40 transition-all hover:-translate-y-0.5 active:scale-95"
                  data-testid="hero-cta-buy">
                  {loadingPayment ? 'Redirection...' : 'Demarrer pour 99\u20AC'} <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Link to="/spin/demo">
                  <Button className="bg-white hover:bg-slate-50 text-slate-900 rounded-full px-8 py-6 text-base font-semibold border border-slate-200 shadow-sm w-full sm:w-auto active:scale-95" data-testid="hero-cta-demo">
                    Voir une demo
                  </Button>
                </Link>
              </motion.div>
              <motion.div variants={fadeUp} className="flex flex-wrap gap-x-6 gap-y-2">
                {["Sans abonnement mensuel", "1 support inclus", "Operationnel en 24h"].map((t, i) => (
                  <span key={i} className="flex items-center gap-2 text-sm text-slate-500">
                    <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center"><Check className="w-3 h-3 text-emerald-600" /></span>
                    {t}
                  </span>
                ))}
              </motion.div>
            </motion.div>

            {/* WHEEL */}
            <motion.div initial={{ opacity: 0, scale: 0.85 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.9, delay: 0.3, type: 'spring', stiffness: 70 }}
              className="relative flex justify-center lg:justify-end">
              <div className="relative">
                <div className="absolute inset-[-40px] rounded-full opacity-20 blur-[60px] -z-10" style={{ background: 'radial-gradient(circle, #F59E0B 0%, #7C3AED 60%, transparent 80%)' }} />
                <SpinWheel slots={DEMO_SLOTS} size={typeof window !== 'undefined' && window.innerWidth < 768 ? 240 : 340} onSpinEnd={() => {}} preview={true} />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══ SOCIAL PROOF ═══ */}
      <section className="relative z-10 py-10 border-y border-slate-200/60 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            {[
              { n: 2500, s: "+", label: "Commercants actifs" },
              { n: 180, s: "K+", label: "Spins realises" },
              { n: 45, s: "K+", label: "Avis Google generes" },
              { n: 98, s: "%", label: "Clients satisfaits" }
            ].map((s, i) => (
              <div key={i}>
                <p className="text-3xl md:text-4xl font-black text-slate-900" style={{ fontFamily: 'Outfit' }}>
                  <AnimatedCounter target={s.n} suffix={s.s} />
                </p>
                <p className="text-xs text-slate-500 mt-1 font-medium">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section className="py-24 md:py-32 px-4 sm:px-6 md:px-12 relative z-10 bg-[#FAFAFA]" data-testid="how-it-works-section">
        <div className="max-w-5xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600 mb-4">Comment ca marche</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900" style={{ fontFamily: 'Outfit' }}>
              3 etapes. <span className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] bg-clip-text text-transparent">C'est tout.</span>
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={stagger} className="grid md:grid-cols-3 gap-6">
            {[
              { num: "01", icon: Smartphone, title: "Le client scanne le QR code", desc: "Place a votre caisse ou sur vos tables, le QR code attire l'attention immediatement.", color: "#7C3AED" },
              { num: "02", icon: Star, title: "Il laisse un avis ou s'abonne", desc: "Avis Google, follow Instagram ou Facebook — votre visibilite explose.", color: "#F59E0B" },
              { num: "03", icon: Sparkles, title: "Il tourne la roue et gagne !", desc: "Animation spectaculaire, suspense garanti. Le client repart avec le sourire.", color: "#10B981" }
            ].map((step, i) => (
              <motion.div key={i} variants={fadeUp}
                className="relative bg-white rounded-2xl md:rounded-3xl p-8 border border-slate-200/60 shadow-sm hover:shadow-xl hover:shadow-slate-200/50 hover:-translate-y-1 transition-all duration-300 group overflow-hidden">
                <div className="absolute -top-6 -left-2 text-[100px] font-black text-slate-100 leading-none select-none -z-0" style={{ fontFamily: 'Outfit' }}>{step.num}</div>
                <div className="relative z-10">
                  <div className="w-14 h-14 rounded-2xl flex items-center justify-center mb-5 transition-transform group-hover:scale-110"
                    style={{ background: `${step.color}10`, border: `1px solid ${step.color}20` }}>
                    <step.icon className="w-7 h-7" style={{ color: step.color }} />
                  </div>
                  <h3 className="text-lg font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>{step.title}</h3>
                  <p className="text-sm text-slate-500 leading-relaxed">{step.desc}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ FEATURES ═══ */}
      <section className="py-24 md:py-32 px-4 sm:px-6 md:px-12 relative z-10 bg-white" data-testid="features-section">
        <div className="max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-80px" }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600 mb-4">Fonctionnalites</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Tout ce dont vous avez besoin.
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true, margin: "-50px" }} variants={stagger}
            className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map((f, i) => (
              <motion.div key={i} variants={fadeUp}
                className="bg-white rounded-2xl p-7 border border-slate-200/60 shadow-sm hover:shadow-xl hover:shadow-slate-200/50 hover:-translate-y-1 transition-all duration-300 group cursor-default">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-transform group-hover:scale-110"
                  style={{ background: `${f.color}10`, border: `1px solid ${f.color}20` }}>
                  <f.icon className="w-6 h-6" style={{ color: f.color }} />
                </div>
                <h3 className="text-base font-bold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>{f.title}</h3>
                <p className="text-sm text-slate-500 leading-relaxed">{f.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ PRICING ═══ */}
      <section id="pricing" className="py-24 md:py-32 px-4 sm:px-6 md:px-12 relative z-10 bg-[#FAFAFA]" data-testid="pricing-section">
        <div className="max-w-lg mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-12">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600 mb-4">Tarif</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Un seul prix. <span className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] bg-clip-text text-transparent">Tout inclus.</span>
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={scaleIn}
            className="relative bg-white rounded-3xl border border-slate-200/60 shadow-2xl shadow-amber-500/10 overflow-hidden" data-testid="pricing-card">
            <div className="p-8 md:p-12 text-center">
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-amber-50 border border-amber-200 mb-8">
                <Sparkles className="w-3.5 h-3.5 text-[#F59E0B]" />
                <span className="text-xs font-bold text-amber-700 tracking-wider uppercase">Offre Standard</span>
              </div>
              <div className="flex items-baseline justify-center gap-1 mb-2">
                <span className="text-7xl font-black text-slate-900" style={{ fontFamily: 'Outfit' }}>99</span>
                <span className="text-3xl font-bold text-[#F59E0B]">{'\u20AC'}</span>
              </div>
              <p className="text-slate-500 mb-8 text-sm">Paiement unique — <span className="text-slate-900 font-semibold">pas d'abonnement, jamais.</span></p>
              <ul className="text-left max-w-xs mx-auto space-y-3.5 mb-10">
                {["Roue 100% personnalisee", "QR Code unique telechargeable", "Dashboard complet + stats", "Base de donnees clients auto", "Campagnes Email & SMS integrees", "Systeme anti-triche integre", "Export CSV illimite", "1h de support personnalise"].map((item, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-700">
                    <span className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                      <Check className="w-3 h-3 text-emerald-600" />
                    </span>
                    {item}
                  </li>
                ))}
              </ul>
              <Button onClick={handlePayment} disabled={loadingPayment}
                className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-6 text-base font-bold shadow-xl shadow-amber-500/20 transition-all hover:-translate-y-0.5 active:scale-95"
                data-testid="pricing-cta">
                {loadingPayment ? 'Redirection...' : 'Commander maintenant'} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <p className="text-xs text-slate-400 mt-4">Paiement securise par Stripe. Satisfait ou rembourse 30 jours.</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══ TESTIMONIALS ═══ */}
      <section className="py-24 md:py-32 px-4 sm:px-6 md:px-12 relative z-10 bg-white" data-testid="testimonials-section">
        <div className="max-w-6xl mx-auto">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-16">
            <motion.p variants={fadeUp} className="text-xs tracking-[0.25em] uppercase font-bold text-amber-600 mb-4">Temoignages</motion.p>
            <motion.h2 variants={fadeUp} className="text-3xl md:text-4xl lg:text-5xl font-black text-slate-900" style={{ fontFamily: 'Outfit' }}>
              Ils ont <span className="bg-gradient-to-r from-[#7C3AED] to-[#F59E0B] bg-clip-text text-transparent">multiplie</span> leurs avis Google
            </motion.h2>
          </motion.div>
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="grid md:grid-cols-3 gap-6">
            {TESTIMONIALS.map((t, i) => (
              <motion.div key={i} variants={fadeUp}
                className="bg-white rounded-2xl p-7 border border-slate-200/60 shadow-sm hover:shadow-xl hover:shadow-slate-200/50 hover:-translate-y-1 transition-all duration-300">
                <div className="flex items-center justify-between mb-5">
                  <div className="flex gap-0.5">
                    {Array(t.stars).fill(0).map((_, j) => <Star key={j} className="w-4 h-4 fill-[#F59E0B] text-[#F59E0B]" />)}
                  </div>
                  <span className="text-xs font-bold text-emerald-700 bg-emerald-50 px-3 py-1 rounded-full border border-emerald-200">{t.metric}</span>
                </div>
                <p className="text-sm text-slate-600 mb-6 leading-relaxed">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <img src={t.img} alt={t.name} className="w-11 h-11 rounded-full object-cover ring-2 ring-slate-200" />
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{t.name}</p>
                    <p className="text-xs text-slate-500">{t.role}</p>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-24 md:py-32 px-4 sm:px-6 md:px-12 relative z-10 bg-[#FAFAFA]">
        <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={scaleIn}
          className="max-w-3xl mx-auto text-center bg-white rounded-3xl p-12 md:p-16 relative overflow-hidden border border-slate-200/60 shadow-2xl shadow-slate-200/50">
          <div className="relative z-10">
            <h2 className="text-3xl md:text-4xl font-black text-slate-900 mb-4" style={{ fontFamily: 'Outfit' }}>
              Pret a booster votre business ?
            </h2>
            <p className="text-slate-500 mb-8 max-w-md mx-auto">
              Rejoignez les milliers de commercants qui ont deja transforme leurs clients en ambassadeurs.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Button onClick={handlePayment} disabled={loadingPayment}
                className="bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-10 py-6 text-base font-bold shadow-xl shadow-amber-500/25 active:scale-95"
                data-testid="final-cta-buy">
                Commencer pour 99{'\u20AC'} <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
              <Link to="/spin/demo">
                <Button className="bg-white hover:bg-slate-50 text-slate-900 rounded-full px-10 py-6 text-base font-semibold border border-slate-200 shadow-sm active:scale-95">
                  Voir la demo
                </Button>
              </Link>
            </div>
          </div>
        </motion.div>
      </section>

      {/* ═══ FOOTER ═══ */}
      <footer className="bg-slate-900 py-12 px-4 sm:px-6 md:px-12 relative z-10" data-testid="footer">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-2.5">
            <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#7C3AED] to-[#F59E0B] flex items-center justify-center font-bold text-xs text-white">S</div>
            <span className="font-bold text-lg text-white" style={{ fontFamily: 'Outfit' }}>Spin Review</span>
          </div>
          <div className="flex gap-6 text-sm text-slate-400">
            <span className="hover:text-white cursor-pointer transition-colors">Mentions legales</span>
            <span className="hover:text-white cursor-pointer transition-colors">Politique de confidentialite</span>
            <span className="hover:text-white cursor-pointer transition-colors">Contact</span>
          </div>
          <p className="text-xs text-slate-500">&copy; 2026 Spin Review. Tous droits reserves.</p>
        </div>
      </footer>
    </div>
  );
}
